import { useEffect, useRef, useState } from 'react';
import { BRAND } from '@tarot/shared';
import { isToss } from '../../lib/platform';
import { canRequestTossReview, requestTossReview } from '../../lib/toss';
import { track } from '../../lib/track';
import { PrimaryButton, SecondaryButton } from '../../components/Buttons';
import { StarMark } from '../../brand/BrandLogo';
import { useMe } from '../auth/useMe';
import { markReviewDone, markReviewShown, noteReadingRevealed, snoozeReview } from './reviewPrompt';

/**
 * 앱인토스 미니앱 리뷰 부탁 — **토스 안에서만** 보인다.
 *
 * 미니앱은 노출과 추천이 지표를 따라가고, 리뷰는 우리가 만들 수 없으면서 순위에 직접 닿는
 * 몇 안 되는 값이다. 그래서 부탁하되, 부탁하는 방식은 두 가지를 지킨다:
 *
 *  - **끼어들지 않는다.** 리뷰 창을 우리가 알아서 띄우지 않고, 사용자가 누를 때만 연다.
 *    해석을 읽는 중에 창이 뜨면 본문을 가리고, 그렇게 받은 별점은 낮다
 *  - **한 화면에 부탁은 하나만.** 비회원에게는 이 자리에 저장 유도(SaveCta)가 이미 서 있다.
 *    회원 전환이 리뷰보다 값지므로 그 자리를 뺏지 않는다
 *
 * 언제 물어도 되는지는 [reviewPrompt.ts](./reviewPrompt.ts)가 정한다. 별점·후기는 토스에
 * 남고 우리에게 오지 않는다 — 우리가 세는 건 부탁을 몇 번 했고 몇 번 눌렀는지뿐이다.
 */
export function ReviewCta() {
  const { me } = useMe();
  const [ask, setAsk] = useState(false);
  /** 리뷰 창을 못 띄웠다 — 눌렀는데 아무 일도 없는 것보다 이유를 적는 편이 낫다 */
  const [failed, setFailed] = useState(false);
  const counted = useRef(false);

  useEffect(() => {
    // 이 컴포넌트가 붙는 순간 = 해석을 끝까지 본 순간. 웹에서는 셀 이유도 없다
    if (!isToss || counted.current) return;
    counted.current = true; // StrictMode 이중 마운트에도 한 번만 센다
    if (!noteReadingRevealed()) return;
    // 구버전 토스 앱(5.253.0 미만)에는 눌러도 열리지 않는 버튼을 보여주지 않는다
    void canRequestTossReview().then(setAsk);
  }, []);

  const shown = ask && !!me;

  useEffect(() => {
    if (!shown) return;
    markReviewShown(); // 그냥 지나가도 한동안은 다시 묻지 않는다
    track('review:prompt');
  }, [shown]);

  if (!shown) return null;

  const onWrite = async () => {
    if (await requestTossReview()) {
      markReviewDone();
      track('review:open');
      setAsk(false);
    } else {
      setFailed(true);
    }
  };

  const onLater = () => {
    snoozeReview();
    setAsk(false);
  };

  return (
    <section className="flex flex-col items-center gap-2.5 rounded-2xl border border-white/10 bg-white/5 p-4 text-center">
      <p className="flex items-center gap-1.5 text-sm font-semibold">
        <StarMark className="h-5 w-5 text-gold" />
        {BRAND.nameKo}, 어떠셨어요?
      </p>
      <p className="text-xs leading-relaxed text-moon-dim">
        리뷰 한 줄이 다음 사람에게 큰 도움이 돼요.
        <br />
        별점과 후기는 <b className="text-moon">토스에 남고</b>, 우리는 내용을 따로 보관하지 않아요.
      </p>
      {failed && (
        <p className="text-xs text-red-300">
          ⚠️ 지금은 리뷰 창을 열 수 없어요. 잠시 후 다시 시도해 주세요.
        </p>
      )}
      <div className="flex w-full max-w-xs gap-2">
        <SecondaryButton onClick={onLater}>다음에</SecondaryButton>
        <PrimaryButton onClick={() => void onWrite()}>리뷰 남기기</PrimaryButton>
      </div>
    </section>
  );
}

/**
 * 내 정보 안의 **상시 입구** — 토스 미니앱에서만 보인다.
 *
 * 결과 화면의 부탁(위 `ReviewCta`)은 조건이 맞을 때만 뜨고, 한 번 넘기면 한동안 사라진다.
 * 나중에 마음이 바뀐 사람이 찾아올 자리가 하나는 있어야 해서 여기에도 둔다.
 * 여기서 리뷰 창까지 가면 결과 화면에서는 더 이상 묻지 않는다.
 */
export function ReviewCard() {
  const [ready, setReady] = useState(false);
  const [opened, setOpened] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    void canRequestTossReview().then(setReady);
  }, []);

  if (!ready) return null;

  const onWrite = async () => {
    if (await requestTossReview()) {
      markReviewDone(); // 결과 화면에서는 더 묻지 않는다
      track('review:open');
      setOpened(true);
    } else {
      setFailed(true);
    }
  };

  return (
    <section className="flex flex-col gap-3 rounded-3xl border border-white/10 bg-white/5 p-4">
      <h3 className="text-sm font-semibold text-moon-dim">리뷰</h3>
      <p className="text-xs leading-relaxed text-moon-dim">
        {opened
          ? '고마워요. 남겨주신 이야기가 다음 사람에게 닿아요.'
          : `${BRAND.nameKo}를 쓰면서 느낀 점을 토스에 남겨주시면 큰 도움이 돼요.`}
      </p>
      {failed && (
        <p className="text-xs text-red-300">
          ⚠️ 지금은 리뷰 창을 열 수 없어요. 잠시 후 다시 시도해 주세요.
        </p>
      )}
      {!opened && (
        <SecondaryButton onClick={() => void onWrite()}>
          <StarMark className="h-4 w-4 text-gold" /> 리뷰 남기기
        </SecondaryButton>
      )}
    </section>
  );
}
